import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanFace, Images, Heart, MapPin, CalendarDays, Camera, ArrowRight } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import { Logo } from "@/components/common/Logo";

export default function ClientEvent() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get(`/public/events/${eventId}`).then((r) => setData(r.data)).catch(() => setErr(true)); }, [eventId]);

  if (err) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-cream">
      <Logo light /><h1 className="font-serif text-3xl text-ink mt-8">Gallery not found</h1>
      <p className="text-ink/60 mt-2">This event link may be invalid or expired.</p>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-cream"><Loader full label="Opening your gallery" /></div>;
  const e = data.event;

  const actions = [
    { to: `/event/${eventId}/find`, icon: ScanFace, title: "Find My Photos", desc: "Upload a selfie & discover every moment you're in", primary: true },
    { to: `/event/${eventId}/gallery`, icon: Images, title: "Browse All Photos", desc: `Explore all ${e.photo_count} photographs` },
    { to: `/event/${eventId}/selected`, icon: Heart, title: "My Selected Photos", desc: "Review the photos you've chosen for your album" },
  ];

  return (
    <div className="relative min-h-screen bg-background overflow-hidden">
      <div className="absolute inset-0">
        {e.cover_image && <img src={mediaUrl(e.cover_image)} alt="" className="w-full h-full object-cover ken-burns" />}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />
      </div>

      <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 py-6"><Logo /></div>

        <div className="max-w-3xl mx-auto px-6 pt-10 sm:pt-20 pb-24 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{e.event_type} · {data.photographer?.name}</div>
            <h1 className="font-serif text-5xl sm:text-7xl text-white font-light leading-none">{e.name}</h1>
            <div className="flex items-center justify-center gap-5 text-sm text-white/70 mt-6">
              {e.event_date && <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{e.event_date}</span>}
              {e.venue && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{e.venue}</span>}
            </div>
            <p className="font-serif text-2xl sm:text-3xl text-champagne/90 italic mt-10">Your memories are waiting.</p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-4 mt-12">
            {actions.map((a, i) => (
              <motion.button key={a.to} data-testid={`client-action-${i}`} onClick={() => navigate(a.to)}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1 }}
                className={`group text-left rounded-2xl p-6 hover-lift transition-colors ${a.primary ? "bg-gold text-black" : "glass border-white/10 hover:border-gold/40"}`}>
                <a.icon className={`w-7 h-7 mb-4 ${a.primary ? "text-black" : "text-gold"}`} />
                <div className={`font-medium ${a.primary ? "text-black" : "text-white"}`}>{a.title}</div>
                <div className={`text-xs mt-1.5 ${a.primary ? "text-black/70" : "text-muted-foreground"}`}>{a.desc}</div>
                <div className={`mt-4 inline-flex items-center gap-1 text-xs font-medium ${a.primary ? "text-black" : "text-gold"}`}>Open <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" /></div>
              </motion.button>
            ))}
          </div>

          <div className="mt-10 inline-flex items-center gap-2 text-xs text-muted-foreground glass rounded-full px-4 py-2">
            <Camera className="w-3.5 h-3.5 text-gold" /> Powered by PMR Events — AI face discovery. Your face data is private & encrypted.
          </div>
        </div>
      </div>
    </div>
  );
}
